import { apiSlice } from '../apiSlice';
import {
	ClickPageDTO,
	ClockPageFilter,
} from '../DTO/app-setting/clock.interface';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	AddRequest,
	AddSingleRequest,
	MeditationPageDTO,
	MeditationPageFilter,
	MeditationSinglePageDTO,
	MeditationSinglePageFilter,
	UnitSeriesDetailResDTO,
	UnitSeriesPublishDTO,
	UnitSeriesSinglePageResDTO,
	UnitSingleDetailResDTO,
	UnitSinglePublishDTO,
} from '../DTO/exercise/meditation.interface';

export const meditationApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getMeditationList: builder.query<
			SuccessResponse<Paginated<MeditationPageDTO>>,
			MeditationPageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['MeditationList'],
		}),
		getMeditationSortList: builder.query<
			SuccessResponse<MeditationPageDTO>,
			null
		>({
			query: () => ({
				url: `/web/exercise/unitMeditation/series/sort`,
				method: 'GET',
			}),
			providesTags: ['MeditationSortList'],
		}),
		setMeditationSort: builder.mutation<
			SuccessResponse<any>,
			{ seriesIdList: string[] }
		>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MeditationList', 'MeditationSortList'],
		}),
		getMeditationPublishingList: builder.query<
			SuccessResponse<MeditationPageDTO[]>,
			null
		>({
			query: () => ({
				url: `/web/exercise/unitMeditation/series/published`,
				method: 'GET',
			}),
		}),
		setMeditationStatus: builder.mutation<
			SuccessResponse<any>,
			{ seriesPublishDTOList: UnitSeriesPublishDTO[] }
		>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MeditationList', 'MeditationDetail'],
		}),
		getMeditationDetail: builder.query<
			SuccessResponse<UnitSeriesDetailResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/unitMeditation/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['MeditationDetail'],
		}),
		addMeditation: builder.mutation<SuccessResponse<any>, AddRequest>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['MeditationList'],
		}),
		updateMeditation: builder.mutation<SuccessResponse<any>, AddRequest>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MeditationDetail'],
		}),
		deleteMeditation: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/unitMeditation/series/${id}`,
				method: 'DELETE',
			}),
		}),
		getMeditationSingleList: builder.query<
			SuccessResponse<Paginated<MeditationSinglePageDTO>>,
			MeditationSinglePageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/${body.id}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['MeditationSingleList'],
		}),
		addSingle: builder.mutation<SuccessResponse<any>, AddSingleRequest>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/${body.seriesId}`,
				method: 'POST',
				body,
			}),
		}),
		updateSingle: builder.mutation<SuccessResponse<any>, AddSingleRequest>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/single/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MeditationSingleDetail'],
		}),
		getSingleDetail: builder.query<
			SuccessResponse<UnitSingleDetailResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/unitMeditation/single/${id}`,
				method: 'GET',
			}),
			providesTags: ['MeditationSingleDetail'],
		}),
		getSingleStatusList: builder.query<
			SuccessResponse<UnitSeriesSinglePageResDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/unitMeditation/series/${id}/published`,
				method: 'GET',
			}),
		}),
		setSingleStatus: builder.mutation<
			SuccessResponse<any>,
			{ singlePublishDTOList: UnitSinglePublishDTO[]; id: string }
		>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/${body.id}/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MeditationSingleList', 'MeditationSingleDetail'],
		}),
		getSingleSortList: builder.query<
			SuccessResponse<UnitSeriesSinglePageResDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/unitMeditation/series/${id}/sort`,
				method: 'GET',
			}),
			providesTags: ['MeditationSingleSortList'],
		}),
		setSingleSort: builder.mutation<
			SuccessResponse<any>,
			{ singleIdList: string[]; id: string }
		>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/${body.id}/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MeditationSingleSortList', 'MeditationSingleList'],
		}),
		deleteSingle: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/unitMeditation/single/${id}`,
				method: 'DELETE',
			}),
		}),
		batchUpdateMeditationHasNew: builder.mutation<
			SuccessResponse<any>,
			{ list: any[] }
		>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/has-new`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MeditationList'],
		}),
		batchUpdateSingleMeditationHasNew: builder.mutation<
			SuccessResponse<any>,
			{ id: string; list: any[] }
		>({
			query: (body) => ({
				url: `/web/exercise/unitMeditation/series/${body.id}/has-new`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MeditationSingleList'],
		}),
	}),
});

export const {
	useGetMeditationListQuery,
	useGetMeditationSortListQuery,
	useGetMeditationPublishingListQuery,
	useSetMeditationStatusMutation,
	useGetMeditationDetailQuery,
	useAddMeditationMutation,
	useUpdateMeditationMutation,
	useDeleteMeditationMutation,
	useGetMeditationSingleListQuery,
	useAddSingleMutation,
	useUpdateSingleMutation,
	useGetSingleDetailQuery,
	useGetSingleStatusListQuery,
	useSetSingleStatusMutation,
	useSetMeditationSortMutation,
	useGetSingleSortListQuery,
	useSetSingleSortMutation,
	useDeleteSingleMutation,
	useBatchUpdateMeditationHasNewMutation,
	useBatchUpdateSingleMeditationHasNewMutation,
} = meditationApiSlice;
